import { useEffect, useRef, useState } from 'react'
import { BAND_CONTACTS_QUERY, BAND_PROCEDURES_QUERY } from './graphql'
import { COHORT_PAGE, CohortRead, readAllPages } from './cohortRead'
import {
  ProcedureRow,
  RowGroup,
  chunkPatientIds,
  groupsPatientIdsToRead,
  keepsPatientInGroups,
  rowSearch,
  sortContacts,
} from './procedureFilter'
import type { ActionCenterBandContactsQuery, ActionCenterBandProceduresQuery } from 'app/generated/gql/graphql'
import { gqlQuery } from 'app/lib/graphqlClient'
import { TreatmentContact } from 'lib/treatmentCards'
import { logError } from 'lib/logging'

type Result = CohortRead<TreatmentContact>

const IDLE: Result = { ready: true, contacts: [] }
const READING: Result = { ready: false, contacts: [] }

/**
 * The band cut by what the treatment is.
 *
 * Each row is asked of the procedures index first, as the set of patients
 * with a pending line that matches. Then the band: for positive rows only,
 * its own search narrowed to those patients, a chunk at a time so no term
 * nears the clause ceiling; with a negative row in play, the whole band,
 * since "has none of these" is not something the index can be handed as a
 * list. Either way every survivor is held here and paged locally, so the
 * count is the count of who is drawn.
 *
 * `cohort` is the schedule split's result when that is also on: the band has
 * then already been read whole and cut by the diary, and this cuts that list
 * rather than reading the index a second time. Its own reads wait for it.
 */
export function useProcedureFilter(
  enabled: boolean,
  {
    groups,
    search,
    sortField,
    direction,
    locationTerm,
    cohort,
  }: {
    /** Every group must hold; each keeps its own AND/OR (see `RowGroup`). */
    groups: RowGroup[]
    search: string
    sortField: `lastTreatmentPlanDate` | `unbookedTreatmentValue`
    direction: `ASC` | `DESC`
    locationTerm?: string
    cohort?: Result
  },
): Result {
  const [result, setResult] = useState<Result>(enabled ? READING : IDLE)
  // Counts the runs of the effect below. Each run takes the next number as its
  // token and checks it against the counter at every await: a later run
  // (new rows, band or sort) bumps the counter, so an older read still in
  // flight sees a mismatch, stops, and never writes a stale list over the
  // fresh one. Same guard as `useScheduleSplit` and `useBandCards`.
  const generation = useRef(0)
  // The groups are rebuilt from the URL on every render; their contents are what matter.
  const groupsKey = JSON.stringify(groups)
  const cohortReady = cohort?.ready ?? true
  const cohortContacts = cohort?.contacts
  const cohortError = cohort?.error

  useEffect(() => {
    const token = ++generation.current
    if (!enabled) {
      setResult(IDLE)
      return
    }
    setResult(READING)
    if (!cohortReady) return
    if (cohortError) {
      setResult({ ready: true, contacts: [], error: cohortError })
      return
    }

    const readContacts = (contactSearch: string) =>
      readAllPages<TreatmentContact>(`band`, async (index) => {
        const result = await gqlQuery<ActionCenterBandContactsQuery>(BAND_CONTACTS_QUERY, {
          search: contactSearch,
          pagination: { limit: COHORT_PAGE, page: index },
          sort: { field: sortField, direction },
        })
        if (result.error) throw result.error
        if (token !== generation.current) return undefined
        const data = result.data?.contacts
        return { hits: data?.hits ?? [], total: data?.total ?? 0 }
      })

    const run = async () => {
      // One read per row, fired together: none depends on another.
      const readRow = async (row: ProcedureRow) => {
        const procedureSearch = [`state:"PENDING"`, rowSearch(row), ...(locationTerm ? [locationTerm] : [])].join(
          ` AND `,
        )
        const hits = await readAllPages<{ patientId?: string | null }>(`procedures`, async (index) => {
          const result = await gqlQuery<ActionCenterBandProceduresQuery>(BAND_PROCEDURES_QUERY, {
            search: procedureSearch,
            pagination: { limit: COHORT_PAGE, page: index },
          })
          if (result.error) throw result.error
          if (token !== generation.current) return undefined
          const data = result.data?.procedures
          return { hits: data?.hits ?? [], total: data?.total ?? 0 }
        })
        return new Set(hits.map((hit) => hit.patientId).filter(Boolean) as string[])
      }
      const sets = await Promise.all(groups.map((group) => Promise.all(group.rows.map(readRow))))
      if (token !== generation.current) return

      let contacts: TreatmentContact[]
      const patientIds = groupsPatientIdsToRead(groups, sets)
      if (cohortContacts) {
        contacts = cohortContacts
      } else if (!patientIds) {
        // A negative row cannot be handed to the index as a term (a NOT list
        // has the same 1,024-clause ceiling), so the band is read whole.
        contacts = await readContacts(search)
      } else {
        const chunks = chunkPatientIds(patientIds)
        const pages = await Promise.all(
          chunks.map((ids) => readContacts(`${search} patientIds:(${ids.map((id) => `"${id}"`).join(` OR `)})`)),
        )
        contacts = pages.flat()
      }
      if (token !== generation.current) return

      const kept = contacts.filter((contact) => keepsPatientInGroups(contact.patientIds ?? [], groups, sets))
      setResult({ ready: true, contacts: sortContacts(kept, sortField, direction) })
    }

    run().catch((err) => {
      if (token !== generation.current) return
      logError(`Action Center: treatment procedure filter`, err)
      setResult({ ready: true, contacts: [], error: `These cases could not be loaded.` })
    })
    // `groups` is tracked through `groupsKey`.
  }, [enabled, groupsKey, search, sortField, direction, locationTerm, cohortReady, cohortContacts, cohortError])

  return result
}
